/**
 * Toast notification system.
 *
 * Non-blocking, stackable notifications that auto-dismiss.
 * Errors/warnings use role="alert" (assertive); info/success use role="status" (polite).
 */

const VALID_TYPES = new Set(['error', 'warning', 'info', 'success']);

const DEFAULTS = {
  type: 'error',
  duration: 5000,
};

/**
 * Show a toast notification.
 *
 * @param {string} message - The notification message
 * @param {Object} [options]
 * @param {'error'|'warning'|'info'|'success'} [options.type='error'] - Notification type
 * @param {number} [options.duration=5000] - Auto-dismiss after ms (0 = no auto-dismiss)
 * @returns {HTMLElement} The notification element (for programmatic dismissal)
 */
export function showNotification(message, options = {}) {
  const { type: rawType, duration } = { ...DEFAULTS, ...options };
  const type = VALID_TYPES.has(rawType) ? rawType : DEFAULTS.type;

  const container = document.getElementById('notifications');
  if (!container) {
    console.warn('[notification-utils] #notifications container not found');
    return null;
  }

  const el = document.createElement('div');
  el.className = `notification notification-${type}`;

  // Errors/warnings are assertive (role="alert"); info/success are polite (role="status")
  el.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');
  el.textContent = message;

  // Dismiss button
  const dismissBtn = document.createElement('button');
  dismissBtn.className = 'notification-dismiss';
  dismissBtn.setAttribute('aria-label', 'Dismiss notification');
  dismissBtn.textContent = '\u00d7'; // ×
  dismissBtn.addEventListener('click', () => dismissNotification(el));
  el.appendChild(dismissBtn);

  container.appendChild(el);

  // Auto-dismiss
  if (duration > 0) {
    el._dismissTimer = setTimeout(() => dismissNotification(el), duration);
  }

  return el;
}

/**
 * Dismiss a notification with exit animation.
 *
 * @param {HTMLElement} el - The notification element to dismiss
 */
export function dismissNotification(el) {
  if (!el || !el.parentNode) return;

  if (el._dismissTimer) {
    clearTimeout(el._dismissTimer);
    el._dismissTimer = null;
  }

  el.classList.add('notification-exit');

  // Fallback removal if animationend doesn't fire (e.g., animations disabled)
  const fallbackTimer = setTimeout(() => {
    if (el.parentNode) el.remove();
  }, 500);

  el.addEventListener('animationend', () => {
    clearTimeout(fallbackTimer);
    el.remove();
  }, { once: true });
}
